import { DATE_DISPLAY_LABELS } from '../../../shared/appConstants';
import { getLocalDatePart } from '../../../shared/timeUtils';

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const normalizeDateString = (raw) => {
  if (!raw) return null;
  if (raw instanceof Date) {
    return formatDate(raw);
  }
  const dateStr = String(raw).includes('T') ? getLocalDatePart(raw) : raw;
  if (dateStr.includes('/')) {
    const parts = dateStr.split('/').map(Number);
    if (parts.length === 3) {
      const [p1, p2, p3] = parts;
      const year = String(p1).length === 4 ? p1 : p3;
      const month = String(p1).length === 4 ? p2 : p1;
      const day = String(p1).length === 4 ? p3 : p2;
      if (year && month && day) {
        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      }
    }
  }
  if (dateStr.includes('-')) {
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const [year, month, day] = parts;
      if (year && month && day) {
        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      }
    }
  }
  return dateStr;
};

export const parseDateLocal = (raw) => {
  if (!raw) return null;
  if (raw instanceof Date) {
    return new Date(raw.getFullYear(), raw.getMonth(), raw.getDate());
  }
  const normalized = normalizeDateString(String(raw));
  if (!normalized) return null;
  const [year, month, day] = normalized.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

export const sanitizeGanttApiFilters = (filters = {}) => {
  const apiFilters = { ...(filters || {}) };
  if (filters?.startDate) apiFilters.start_date = filters.startDate;
  if (filters?.dueDate) apiFilters.due_date = filters.dueDate;

  const removeKeys = new Set([
    'epicStartDate',
    'epicDueDate',
    'startDate',
    'dueDate',
    'selectedProject',
  ]);

  Object.keys(apiFilters).forEach((key) => {
    if (removeKeys.has(key)) {
      delete apiFilters[key];
    }
  });

  Object.entries(apiFilters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      delete apiFilters[key];
      return;
    }
    if (Array.isArray(value)) {
      if (value.length === 0 || value.includes('all') || value.includes('ALL')) {
        delete apiFilters[key];
      }
      return;
    }
    if (value === 'all' || value === 'ALL') {
      delete apiFilters[key];
    }
  });

  return apiFilters;
};

export const calculateTaskPositions = (tasks, dates, viewType, getTaskDates) => {
  const tasksWithCols = [];

  tasks.forEach(task => {
    if (!task.startDate || !task.dueDate) return;

    const { startDate, dueDate } = getTaskDates(task);
    const normalizedStartDate = normalizeDateString(startDate);
    const normalizedDueDate = normalizeDateString(dueDate);

    const start = parseDateLocal(normalizedStartDate);
    const end = parseDateLocal(normalizedDueDate);

    let startCol = -1;
    let endCol = -1;
    let boundaryInfo = { isIncoming: false, isOutgoing: false };

    if (viewType === "Week") {
      // Check if task extends beyond current view
      const viewStartDate = parseDateLocal(dates[0]);
      const viewEndDate = parseDateLocal(dates[dates.length - 1]);
      
      // For week view, each date represents a week starting from that date (Monday)
      // Calculate the actual end of the last week
      const lastWeekStart = new Date(viewEndDate);
      const lastWeekEnd = new Date(lastWeekStart);
      lastWeekEnd.setDate(lastWeekStart.getDate() + 6); // Sunday
      
      // The first week starts from the first date (which is already a Monday)
      const firstWeekStart = new Date(viewStartDate);
      
      boundaryInfo.isIncoming = start < firstWeekStart;
      boundaryInfo.isOutgoing = end > lastWeekEnd;
      boundaryInfo.actualStartDate = normalizedStartDate;
      boundaryInfo.actualEndDate = normalizedDueDate;

      // Find all weeks that the task overlaps with
      dates.forEach((dateStr, colIndex) => {
        const currentDate = parseDateLocal(dateStr);
        // The dateStr is already the Monday of the week
        const weekStart = new Date(currentDate);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6); // Sunday

        // Check if task overlaps with this week
        if (start <= weekEnd && end >= weekStart) {
          if (startCol === -1) startCol = colIndex;
          endCol = colIndex;
        }
      });
      
      // If task is incoming (starts before view), ensure it starts from first column
      if (boundaryInfo.isIncoming && startCol === -1) {
        startCol = 0;
      }
      
      // If task is outgoing (ends after view), ensure it ends at last column
      if (boundaryInfo.isOutgoing && endCol === -1) {
        endCol = dates.length - 1;
      }
      
      // If task spans entire view but doesn't start/end in visible weeks
      if (startCol === -1 && endCol === -1 && boundaryInfo.isIncoming && boundaryInfo.isOutgoing) {
        startCol = 0;
        endCol = dates.length - 1;
      }
    }

    if (viewType === "Day") {
      // Check if task extends beyond current view
      const viewStartDate = parseDateLocal(dates[0]);
      const viewEndDate = parseDateLocal(dates[dates.length - 1]);
      
      boundaryInfo.isIncoming = start < viewStartDate;
      boundaryInfo.isOutgoing = end > viewEndDate;
      boundaryInfo.actualStartDate = normalizedStartDate;
      boundaryInfo.actualEndDate = normalizedDueDate;

      // Find the actual start and end columns within the visible range
      let actualStartCol = -1;
      let actualEndCol = -1;
      
      dates.forEach((dateStr, colIndex) => {
        const day = parseDateLocal(dateStr);
        
        // Check if this day falls within the task's date range
        if (start <= day && end >= day) {
          if (actualStartCol === -1) actualStartCol = colIndex;
          actualEndCol = colIndex;
        }
      });
      
      // If task is incoming (starts before view), start from first visible column
      if (boundaryInfo.isIncoming && actualStartCol === -1) {
        actualStartCol = 0;
      }
      
      // If task is outgoing (ends after view), end at last visible column
      if (boundaryInfo.isOutgoing && actualEndCol === -1) {
        actualEndCol = dates.length - 1;
      }
      
      // If task spans the entire view but doesn't start/end in visible dates
      if (actualStartCol === -1 && actualEndCol === -1 && boundaryInfo.isIncoming && boundaryInfo.isOutgoing) {
        actualStartCol = 0;
        actualEndCol = dates.length - 1;
      }
      
      startCol = actualStartCol;
      endCol = actualEndCol;
    }

    if (startCol !== -1 && endCol !== -1) {
      tasksWithCols.push({
        id: task.id,
        ticket: task.ticketNumber,
        task: { ...task, startDate: normalizedStartDate, dueDate: normalizedDueDate },
        startCol,
        endCol,
        boundaryInfo
      });
    }
  });

  tasksWithCols.sort((a, b) => a.startCol - b.startCol);

  // Build dependency graph for constraint-based positioning
  const dependencyGraph = new Map();
  const dependents = new Map(); // Maps task -> tasks that depend on it
  
  tasksWithCols.forEach(item => {
    dependencyGraph.set(item.id, []);
    dependents.set(item.id, []);
  });
  
  // Populate dependency relationships
  tasksWithCols.forEach(item => {
    if (item.task.dependencies) {
      item.task.dependencies.forEach(dep => {
        const targetId = dep.targetTaskId;
        if (dependencyGraph.has(targetId)) {
          dependencyGraph.get(item.id).push(targetId);
          dependents.get(targetId).push(item.id);
        }
      });
    }
  });

  const rows = [];
  const taskRowMap = new Map();

  tasksWithCols.forEach(item => {
    let placed = false;
    let minRow = 0;
    
    // Calculate minimum row based on dependencies
    if (item.task.dependencies) {
      item.task.dependencies.forEach(dep => {
        const sourceRow = taskRowMap.get(dep.targetTaskId);
        if (sourceRow !== undefined) {
          minRow = Math.max(minRow, sourceRow + 1);
        }
      });
    }
    
    // Find the first available row starting from minRow
    for (let r = minRow; r < rows.length + 10; r++) { // Limit to prevent infinite loop
      // Ensure row exists
      if (!rows[r]) rows[r] = [];
      
      let overlap = false;
      const itemStart = parseDateLocal(normalizeDateString(item.task.startDate));
      const itemEnd = parseDateLocal(normalizeDateString(item.task.dueDate));

      // Check for overlap with existing tasks in this row
      for (const existing of rows[r]) {
        // Check if date ranges overlap
        const existingStart = parseDateLocal(normalizeDateString(existing.task.startDate));
        const existingEnd = parseDateLocal(normalizeDateString(existing.task.dueDate));
        
        // Tasks overlap if: itemStart <= existingEnd && itemEnd >= existingStart
        const dateOverlap = itemStart <= existingEnd && itemEnd >= existingStart;
        
        // Also check column overlap as backup
        const colOverlap = item.startCol <= existing.endCol && item.endCol >= existing.startCol;
        
        if (dateOverlap || colOverlap) {
          overlap = true;
          break;
        }
      }

      if (!overlap) {
        rows[r].push(item);
        taskRowMap.set(item.id, r);
        placed = true;
        break;
      }
    }

    // Fallback: if still not placed, create a new row
    if (!placed) {
      const newRowIndex = Math.max(minRow, rows.length);
      if (!rows[newRowIndex]) rows[newRowIndex] = [];
      rows[newRowIndex].push(item);
      taskRowMap.set(item.id, newRowIndex);
    }
  });

  const grouped = {};
  dates.forEach((_, i) => (grouped[i] = []));

  tasksWithCols.forEach(item => {
    // For Week and Day views, only add task to its starting column
    // The width calculation will handle spanning multiple columns
    if (viewType === 'Week' || viewType === 'Day') {
      grouped[item.startCol].push({
        ...item,
        rowIndex: taskRowMap.get(item.id)
      });
    } else {
      // For other views, add to starting column
      grouped[item.startCol].push({
        ...item,
        rowIndex: taskRowMap.get(item.id)
      });
    }
  });

  return grouped;
};

export const calculateTaskWidth = (task, dateStr, dates, viewType, getTaskDates) => {
  const { startDate, dueDate } = getTaskDates(task);

  const taskStart = parseDateLocal(startDate);
  const taskEnd = parseDateLocal(dueDate);
  const currentDate = parseDateLocal(dateStr);

  // Account for capsule margin (15px on each side = 30px total)
  const marginOffset = '30px';

  if (viewType === 'Day') {
    // For day view, calculate width based on actual date span within visible range
    // Find the start and end indices within the visible dates
    let visibleStartIndex = -1;
    let visibleEndIndex = -1;
    
    dates.forEach((date, index) => {
      const day = parseDateLocal(date);
      if (taskStart <= day && taskEnd >= day) {
        if (visibleStartIndex === -1) visibleStartIndex = index;
        visibleEndIndex = index;
      }
    });
    
    // If task starts before visible range, start from first column
    if (visibleStartIndex === -1 && taskStart < parseDateLocal(dates[0])) {
      visibleStartIndex = 0;
    }
    
    // If task ends after visible range, end at last column
    if (visibleEndIndex === -1 && taskEnd > parseDateLocal(dates[dates.length - 1])) {
      visibleEndIndex = dates.length - 1;
    }
    
    // If task spans entire view but doesn't start/end in visible dates
    if (visibleStartIndex === -1 && visibleEndIndex === -1) {
      if (taskStart < parseDateLocal(dates[0]) && taskEnd > parseDateLocal(dates[dates.length - 1])) {
        visibleStartIndex = 0;
        visibleEndIndex = dates.length - 1;
      }
    }
    
    if (visibleStartIndex !== -1 && visibleEndIndex !== -1) {
      const spanDays = visibleEndIndex - visibleStartIndex + 1;
      return `calc(${spanDays * 100}% + ${(spanDays - 1) * 1}px - ${marginOffset})`;
    }
    
    return `calc(100% - ${marginOffset})`;
  }

  if (viewType === 'Week') {
    // The currentDate (dateStr) is already the Monday of the week
    const weekStart = new Date(currentDate);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6); // Sunday

    // Check if task starts in this week
    const taskStartsInThisWeek = taskStart >= weekStart && taskStart <= weekEnd;
    // Check if task ends in this week
    const taskEndsInThisWeek = taskEnd >= weekStart && taskEnd <= weekEnd;
    // Check if task spans through this week
    const taskSpansThisWeek = taskStart <= weekStart && taskEnd >= weekEnd;

    // If task both starts and ends in this week, single week width
    if (taskStartsInThisWeek && taskEndsInThisWeek) {
      return `calc(100% - ${marginOffset})`;
    }

    // If task starts in this week (but extends beyond)
    if (taskStartsInThisWeek || taskSpansThisWeek) {
      const startIndex = dates.findIndex(d => {
        const wStart = parseDateLocal(d); // d is already Monday
        return wStart && weekStart && wStart.getTime() === weekStart.getTime();
      });

      if (startIndex === -1) {
        return `calc(100% - ${marginOffset})`;
      }

      let span = 1;

      // Count how many weeks this task spans from this starting week
      for (let i = startIndex + 1; i < dates.length; i++) {
        const nextWeekStart = parseDateLocal(dates[i]); // dates[i] is already Monday
        const nextWeekEnd = new Date(nextWeekStart);
        nextWeekEnd.setDate(nextWeekStart.getDate() + 6); // Sunday

        // If task overlaps with the next week, include it in span
        if (taskEnd >= nextWeekStart) {
          span++;
        } else {
          break;
        }
      }

      const finalWidth = `calc(${span * 100}% + ${(span - 1) * 1}px - ${marginOffset})`;
      return finalWidth;
    }
  }

  return `calc(100% - ${marginOffset})`;
};

// Calculate task position within a week column based on day divisions
export const calculateTaskPositionInWeek = (task, dateStr, getTaskDates) => {
  const { startDate, dueDate } = getTaskDates(task);
  const taskStart = parseDateLocal(startDate);
  const taskEnd = parseDateLocal(dueDate);
  const currentDate = parseDateLocal(dateStr);
  
  // Get week boundaries (Monday to Sunday)
  const weekStart = new Date(currentDate);
  const dayOfWeek = weekStart.getDay();
  const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // If Sunday, go back 6 days; otherwise go to Monday
  weekStart.setDate(currentDate.getDate() + daysToMonday);
  
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6); // Sunday
  
  // Calculate which days within the week the task spans
  const taskStartInWeek = new Date(Math.max(taskStart.getTime(), weekStart.getTime()));
  const taskEndInWeek = new Date(Math.min(taskEnd.getTime(), weekEnd.getTime()));
  
  // Calculate day positions (0-6 for Monday-Sunday)
  const startDayInWeek = Math.max(0, Math.floor((taskStartInWeek - weekStart) / (1000 * 60 * 60 * 24)));
  const endDayInWeek = Math.min(6, Math.floor((taskEndInWeek - weekStart) / (1000 * 60 * 60 * 24)));
  
  // Each day is 1/7 of the column width
  const dayWidth = 100 / 7;
  let leftPosition = startDayInWeek * dayWidth;
  
  // For the width, we need to consider if this task extends beyond this week
  let width;
  
  if (taskEnd <= weekEnd) {
    // Task ends within this week - calculate width within the week
    width = (endDayInWeek - startDayInWeek + 1) * dayWidth;
  } else {
    // Task extends beyond this week - width should go to end of week
    width = (6 - startDayInWeek + 1) * dayWidth; // From start day to Sunday
  }
  
  // For single-day tasks within the week, give them extra width
  if (taskStart >= weekStart && taskEnd <= weekEnd && startDayInWeek === endDayInWeek) {
    const extraWidth = dayWidth * 0.3; // 30% more width
    
    if (startDayInWeek === 0) {
      // First day - expand to the right
      width = Math.min(dayWidth + extraWidth, 2 * dayWidth - 1);
    } else if (startDayInWeek === 6) {
      // Last day - expand to the left
      const newWidth = Math.min(dayWidth + extraWidth, 2 * dayWidth - 1);
      leftPosition = leftPosition - (newWidth - dayWidth);
      width = newWidth;
    } else {
      // Middle days - expand equally on both sides (center)
      const newWidth = Math.min(dayWidth + extraWidth, 2 * dayWidth - 1);
      leftPosition = leftPosition - (newWidth - dayWidth) / 2;
      width = newWidth;
    }
  }
  
  const result = {
    left: `${leftPosition}%`,
    width: `${width}%`
  };
  
  return result;
};

export const calculateDragPosition = (clientX, container, dates, viewType) => {
  if (!container || !dates?.length) return 0;

  const containerRect = container.getBoundingClientRect
    ? container.getBoundingClientRect()
    : container;

  const scrollLeft = container.scrollLeft ?? containerRect.scrollLeft ?? 0;
  const styles = container.getBoundingClientRect ? getComputedStyle(container) : null;
  const paddingLeft = styles ? parseFloat(styles.paddingLeft) || 0 : 0;
  const paddingRight = styles ? parseFloat(styles.paddingRight) || 0 : 0;

  // Position relative to the start of scrollable content (exclude container padding)
  const rawRelativeX = clientX - containerRect.left + scrollLeft - paddingLeft;

  // Try to resolve the actual column under the cursor using DOM measurements.
  const columns = container.querySelectorAll
    ? container.querySelectorAll('.gantt-grid-column')
    : null;

  let columnIndex = 0;
  let columnOffsetLeft = 0;
  let columnWidth = 0;

  if (columns && columns.length) {
    const xInContent = Math.max(0, rawRelativeX);
    const first = columns[0];
    const last = columns[columns.length - 1];
    const minLeft = first.offsetLeft;
    const maxRight = last.offsetLeft + last.offsetWidth;

    if (xInContent < minLeft || xInContent >= maxRight) {
      return null;
    }

    for (let i = 0; i < columns.length; i += 1) {
      const col = columns[i];
      const left = col.offsetLeft;
      const right = left + col.offsetWidth;
      if (xInContent >= left && xInContent < right) {
        columnIndex = i;
        columnOffsetLeft = left;
        columnWidth = col.offsetWidth;
        break;
      }
    }

    if (!columnWidth) {
      return null;
    }
  } else {
    // Fallback: evenly divide the total content width
    const totalWidth = container.scrollWidth ?? containerRect.width;
    const contentWidth = Math.max(1, totalWidth - paddingLeft - paddingRight);
    columnWidth = contentWidth / dates.length;
    const clampedRelativeX = Math.max(0, Math.min(rawRelativeX, contentWidth - 1));
    columnIndex = Math.floor(clampedRelativeX / columnWidth);
    columnOffsetLeft = columnIndex * columnWidth;
  }
  
  if (viewType === 'Week') {
    // For week view, also calculate which day within the week
    const withinColumnX = Math.max(0, rawRelativeX - columnOffsetLeft);
    let dayWidth = columnWidth / 7;
    let dayIndex = 0;

    if (columns && columns.length && columns[columnIndex]) {
      const colStyles = getComputedStyle(columns[columnIndex]);
      const colPaddingLeft = parseFloat(colStyles.paddingLeft) || 0;
      const colPaddingRight = parseFloat(colStyles.paddingRight) || 0;
      const innerWidth = Math.max(1, columnWidth - colPaddingLeft - colPaddingRight);
      const withinInnerX = Math.max(0, Math.min(withinColumnX - colPaddingLeft, innerWidth - 1));
      dayWidth = innerWidth / 7;
      dayIndex = Math.floor(withinInnerX / dayWidth);
    } else {
      dayIndex = Math.floor(withinColumnX / dayWidth);
    }
    
    // Clamp values to valid ranges
    const clampedColumnIndex = Math.max(0, Math.min(columnIndex, dates.length - 1));
    const clampedDayIndex = Math.max(0, Math.min(dayIndex, 6));
    
    return {
      columnIndex: clampedColumnIndex,
      dayIndex: clampedDayIndex
    };
  }
  
  return Math.max(0, Math.min(columnIndex, dates.length - 1));
};

export const calculateDateFromPosition = (position, dates, viewType) => {
  if (viewType === 'Week' && typeof position === 'object') {
    const { columnIndex, dayIndex } = position;
    if (columnIndex < 0 || columnIndex >= dates.length) return null;
    
    // dates[columnIndex] is already the Monday of the week
    const weekStartDate = parseDateLocal(dates[columnIndex]);
    
    // Add the day index to get the target date
    const targetDate = new Date(weekStartDate);
    targetDate.setDate(weekStartDate.getDate() + dayIndex);
    
    return formatDate(targetDate);
  }
  
  const columnIndex = typeof position === 'object' ? position.columnIndex : position;
  if (columnIndex < 0 || columnIndex >= dates.length) return null;
  return dates[columnIndex];
};

export const calculateTaskDuration = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  return Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
};

export const validateTaskDependencies = (task, newStartDate, newEndDate, allTasks) => {
  // Basic validation - can be extended for complex dependency checking
  const start = new Date(newStartDate);
  const end = new Date(newEndDate);
  
  if (start > end) {
    return { valid: false, message: 'Start date cannot be after end date' };
  }
  
  return { valid: true };
};

const formatDayRangeDisplay = (dates) => {
  if (!dates || dates.length === 0) return '';
  const from = parseDateLocal(dates[0]) || new Date(dates[0]);
  const to = parseDateLocal(dates[dates.length - 1]) || new Date(dates[dates.length - 1]);
  if (isNaN(from?.getTime?.()) || isNaN(to?.getTime?.())) {
    return `${dates[0]} - ${dates[dates.length - 1]}`;
  }
  const fromDay = from.getDate();
  const toDay = to.getDate();
  const fromMonth = from.toLocaleDateString('en-US', { month: 'short' });
  const toMonth = to.toLocaleDateString('en-US', { month: 'short' });
  const year = to.getFullYear();
  return `${fromDay} ${fromMonth} - ${toDay} ${toMonth} (${year})`;
};

export const getDayViewDates = (selectedDates) => {
  if (!selectedDates || selectedDates.length === 0) {
    const today = new Date();
    const dates = [];

    for (let i = 0; i < 2; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(formatDate(date));
    }

    return {
      dates,
    headers: dates.map(d =>
      parseDateLocal(d).toLocaleDateString('en-US', {
        weekday: 'short',
        day: 'numeric',
        month: 'short'
      })
      ),
      displayText: formatDayRangeDisplay(dates)
    };
  }

  return {
    dates: selectedDates,
    headers: selectedDates.map(d => {
      const parsed = parseDateLocal(d);
      if (!parsed) return d;
      return parsed.toLocaleDateString('en-US', {
        weekday: 'short',
        day: 'numeric',
        month: 'short'
      });
    }),
    displayText: formatDayRangeDisplay(selectedDates)
  };
};

export const getWeekViewDates = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  const dates = [];
  const headers = [];

  // Start from the first Monday of the month or the Monday before if month doesn't start on Monday
  let currentWeekStart = new Date(firstDay);
  // Adjust to Monday (getDay() returns 0 for Sunday, 1 for Monday, etc.)
  const dayOfWeek = currentWeekStart.getDay();
  const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // If Sunday, go back 6 days; otherwise go to Monday
  currentWeekStart.setDate(firstDay.getDate() + daysToMonday);

  while (currentWeekStart <= lastDay) {
    const weekEnd = new Date(currentWeekStart);
    weekEnd.setDate(currentWeekStart.getDate() + 6); // Sunday

    // Use the actual week start date, not the month-bounded date
    dates.push(formatDate(currentWeekStart));

    const startDay = currentWeekStart.getDate();
    const endDay = weekEnd.getDate();

    const startMonth = currentWeekStart.toLocaleDateString('en-US', { month: 'short' });
    const endMonth = weekEnd.toLocaleDateString('en-US', { month: 'short' });

    headers.push(
      startMonth === endMonth
        ? `${startDay}-${endDay} ${startMonth}`
        : `${startDay} ${startMonth} - ${endDay} ${endMonth}`
    );

    currentWeekStart.setDate(currentWeekStart.getDate() + 7);
  }

  return {
    dates,
    headers,
    displayText: date.toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric'
    })
  };
};

export const getWeekRangeForMonth = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  // Find the first Monday of the month or the Monday before if month doesn't start on Monday
  let weekStart = new Date(firstDay);
  const dayOfWeek = weekStart.getDay();
  const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  weekStart.setDate(firstDay.getDate() + daysToMonday);

  // Find the last Sunday that includes days from this month
  let weekEnd = new Date(lastDay);
  const lastDayOfWeek = weekEnd.getDay();
  const daysToSunday = lastDayOfWeek === 0 ? 0 : 7 - lastDayOfWeek;
  weekEnd.setDate(lastDay.getDate() + daysToSunday);

  return {
    fromDate: formatDate(weekStart),
    toDate: formatDate(weekEnd)
  };
};

export const getQuarterViewDates = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();

  const quarterStartMonth = Math.floor(month / 3) * 3;

  const dates = [];
  const headers = [];

  for (let i = 0; i < 3; i++) {
    const monthDate = new Date(year, quarterStartMonth + i, 1);
    dates.push(formatDate(monthDate));
    headers.push(
      monthDate.toLocaleDateString('en-US', { month: 'long' })
    );
  }

  const quarterNum = Math.floor(month / 3) + 1;

  return {
    dates,
    headers,
    displayText: `${DATE_DISPLAY_LABELS.QUARTER} ${quarterNum} (${year})`
  };
};

export const getQuarterRange = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();

  const quarterStartMonth = Math.floor(month / 3) * 3;

  const fromDate = formatDate(new Date(year, quarterStartMonth, 1));
  const toDate = formatDate(new Date(year, quarterStartMonth + 3, 0));

  return { fromDate, toDate };
};

export const getYearViewDates = (date) => {
  const year = date.getFullYear();

  const dates = [];
  const headers = [];

  for (let i = 0; i < 12; i++) {
    // Create date string directly to avoid timezone issues
    const monthStr = String(i + 1).padStart(2, '0');
    const dateStr = `${year}-${monthStr}-01`;
    dates.push(dateStr);
    
    // Create month name from the month number
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    headers.push(monthNames[i]);
  }

  return {
    dates,
    headers,
    displayText: `${DATE_DISPLAY_LABELS.YEAR} ${year}`
  };
};

/**
 * Calculate precise position within a month based on date
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {number} year - Year of the month
 * @param {number} month - Month (1-12)
 * @returns {number} - Position as fraction (0-1) within the month
 */
const getDatePositionInMonth = (date, year, month) => {
  const [, , day] = date.split('-').map(Number);
  const daysInMonth = new Date(year, month, 0).getDate();
  // Position based on day within month (0 = start of month, 1 = end of month)
  const position = (day - 1) / (daysInMonth - 1);
  
  return position;
};

/**
 * Determine if a task extends beyond the current view boundaries
 * @param {Object} card - Card object with tasks and span info
 * @param {Array} dates - Array of month dates in current view
 * @param {string} viewType - 'Quarter' or 'Year'
 * @returns {Object} - {isIncoming, isOutgoing, actualStartDate, actualEndDate}
 */
const getTaskBoundaryInfo = (card, dates, viewType) => {
  // Find earliest start date and latest due date across all tasks
  let earliestStart = card.tasks[0].startDate;
  let latestDue = card.tasks[0].dueDate;
  
  for (let i = 1; i < card.tasks.length; i++) {
    if (card.tasks[i].startDate < earliestStart) earliestStart = card.tasks[i].startDate;
    if (card.tasks[i].dueDate > latestDue) latestDue = card.tasks[i].dueDate;
  }
  
  // Get view boundaries - first and last month in current view
  const viewStartDate = dates[0]; // First month in view (YYYY-MM-01)
  const viewEndDate = dates[dates.length - 1]; // Last month in view (YYYY-MM-01)
  
  // Extract year-month for comparison (YYYY-MM)
  const getYearMonth = (dateStr) => dateStr.substring(0, 7);
  
  const taskStartYM = getYearMonth(earliestStart);
  const taskEndYM = getYearMonth(latestDue);
  const viewStartYM = getYearMonth(viewStartDate);
  const viewEndYM = getYearMonth(viewEndDate);
  
  // Task is incoming if it starts before the first month of current view
  const isIncoming = taskStartYM < viewStartYM;
  
  // Task is outgoing if it ends after the last month of current view
  const isOutgoing = taskEndYM > viewEndYM;
  
  return {
    isIncoming,
    isOutgoing,
    actualStartDate: earliestStart,
    actualEndDate: latestDue,
    taskStartYM,
    taskEndYM,
    viewStartYM,
    viewEndYM
  };
};

/**
 * Handle boundary arrow navigation to actual start/due dates
 * @param {Object} boundaryInfo - Boundary information from task
 * @param {string} direction - 'prev' for start date, 'next' for due date
 * @param {string} viewType - Current view type
 * @param {Function} dispatch - Redux dispatch function
 * @param {Function} setGanttCurrentDate - Action to set current date
 * @param {Function} setGanttSelectedDates - Action to set selected dates (for Day view)
 * @param {Function} fetchGanttData - Action to fetch gantt data
 * @param {string} projectId - Current project ID
 * @param {Object} filters - Current filters
 */
export const handleBoundaryNavigation = (boundaryInfo, direction, viewType, dispatch, setGanttCurrentDate, setGanttSelectedDates, fetchGanttData, projectId, filters, priorityTicketId = null) => {
  let targetDate;
  
  if (direction === 'prev' && boundaryInfo.isIncoming) {
    // Navigate to actual start date
    targetDate = new Date(boundaryInfo.actualStartDate || boundaryInfo.taskStartDate);
  } else if (direction === 'next' && boundaryInfo.isOutgoing) {
    // Navigate to actual due date
    targetDate = new Date(boundaryInfo.actualEndDate || boundaryInfo.taskEndDate);
  } else {
    return; // Invalid navigation
  }
  
  // Calculate the appropriate date range for the view
  const { fromDate, toDate } = calculateDateRangeForView(targetDate, viewType);
  
  // Update the current date in Redux
  dispatch(setGanttCurrentDate(targetDate.toISOString()));
  
  // For Day view, also update selected dates
  if (viewType === 'Day') {
    const selectedDates = [];
    const startDate = new Date(fromDate);
    const endDate = new Date(toDate);
    
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      selectedDates.push(formatDate(d));
    }
    
    dispatch(setGanttSelectedDates(selectedDates));
  }
  
  // Fetch new data for the target date range
  const apiFilters = sanitizeGanttApiFilters(filters);

  // Add priority ticket if provided
  if (priorityTicketId) {
    apiFilters.priority_ticket = priorityTicketId;
  }
  
  dispatch(fetchGanttData({ 
    projectId, 
    fromDate, 
    toDate, 
    filters: apiFilters, 
    page: 1 
  }));
};


export const calculateMultiMonthPosition = (card, dates, viewType, actualColumnWidth = null) => {
  // Get boundary information
  const boundaryInfo = getTaskBoundaryInfo(card, dates, viewType);
  
  // Find earliest start date and latest due date across all tasks
  let earliestStart = card.tasks[0].startDate;
  let latestDue = card.tasks[0].dueDate;
  
  for (let i = 1; i < card.tasks.length; i++) {
    if (card.tasks[i].startDate < earliestStart) earliestStart = card.tasks[i].startDate;
    if (card.tasks[i].dueDate > latestDue) latestDue = card.tasks[i].dueDate;
  }
  
  // Extract date components for calculations
  const [startYear, startMonth] = earliestStart.split('-').map(Number);
  const [endYear, endMonth] = latestDue.split('-').map(Number);
  
  // Calculate position within start and end months
  const startPositionInMonth = getDatePositionInMonth(earliestStart, startYear, startMonth);
  const endPositionInMonth = getDatePositionInMonth(latestDue, endYear, endMonth);
  
  
  if (viewType === 'Year') {
    // Year view: Pixel-based positioning
    let startColumnBase, endColumnBase;
    
    if (boundaryInfo.isIncoming) {
      // Task starts before view - start from beginning of first column
      startColumnBase = 0;
    } else {
      startColumnBase = card.startCol * actualColumnWidth;
    }
    
    // For outgoing tasks, we need to find which column the end date would be in
    if (boundaryInfo.isOutgoing) {
      // Find the column index for the end month
      const endMonthStr = `${endYear}-${String(endMonth).padStart(2, '0')}-01`;
      let endColumnIndex = dates.findIndex(date => date.substring(0, 7) === endMonthStr.substring(0, 7));
      
      if (endColumnIndex === -1) {
        // End month is not in current view, extend to view boundary
        endColumnBase = dates.length * actualColumnWidth;
      } else {
        // End month is in view, use its position
        endColumnBase = endColumnIndex * actualColumnWidth;
      }
    } else {
      // End column base should be the START of the end column, not the end
      endColumnBase = card.endCol * actualColumnWidth;
    }
    
    // Adjust start and end positions based on date positions within months
    let startOffset = 0;
    let endOffset = 0;
    
    if (!boundaryInfo.isIncoming) {
      // Calculate offset within start month
      startOffset = startPositionInMonth * actualColumnWidth;
    }
    
    if (!boundaryInfo.isOutgoing || (boundaryInfo.isOutgoing && endColumnBase < dates.length * actualColumnWidth)) {
      // Calculate how far into the end month the task goes
      endOffset = endPositionInMonth * actualColumnWidth;
    }
    
    const leftPosition = `${startColumnBase + startOffset + 5}px`;
    const cardWidth = `${(endColumnBase - startColumnBase) + endOffset - startOffset - 10}px`;
    
    return { leftPosition, cardWidth, boundaryInfo };
  } else {
    // Quarter view: Percentage-based positioning with bounds checking
    const columnWidth = 100 / dates.length;
    const maxColumnIndex = dates.length - 1;
    
    let startColumnBase, endColumnBase;
    
    if (boundaryInfo.isIncoming) {
      // Task starts before view - start from beginning
      startColumnBase = 0;
    } else {
      const boundedStartCol = Math.max(0, Math.min(card.startCol, maxColumnIndex));
      startColumnBase = boundedStartCol * columnWidth;
    }
    
    // For outgoing tasks, we need to find which column the end date would be in
    if (boundaryInfo.isOutgoing) {
      // Find the column index for the end month
      const endMonthStr = `${endYear}-${String(endMonth).padStart(2, '0')}-01`;
      let endColumnIndex = dates.findIndex(date => date.substring(0, 7) === endMonthStr.substring(0, 7));
      
      if (endColumnIndex === -1) {
        // End month is not in current view, extend to view boundary
        endColumnBase = 100;
      } else {
        // End month is in view, use its position
        endColumnBase = endColumnIndex * columnWidth;
      }
    } else {
      const boundedEndCol = Math.max(0, Math.min(card.endCol, maxColumnIndex));
      // End column base should be the START of the end column, not the end
      endColumnBase = boundedEndCol * columnWidth;
    }
    
    // Adjust start and end positions based on date positions within months
    let startOffset = 0;
    let endOffset = 0;
    
    if (!boundaryInfo.isIncoming) {
      // Calculate offset within start month
      startOffset = startPositionInMonth * columnWidth;
    }
    
    if (!boundaryInfo.isOutgoing || (boundaryInfo.isOutgoing && endColumnBase < 100)) {
      // Calculate how far into the end month the task goes
      endOffset = endPositionInMonth * columnWidth;
    }
    
    const startPosition = startColumnBase + startOffset;
    const endPosition = Math.min(endColumnBase + endOffset, 100);
    
    const totalWidthPercent = endPosition - startPosition;
    
    const leftPosition = `calc(${startPosition}% + 5px)`;
    const cardWidth = `calc(${Math.max(0, totalWidthPercent)}% - 10px)`;
    
    return { leftPosition, cardWidth, boundaryInfo };
  }
};

// Export helper functions for use in components
export { getTaskBoundaryInfo };

/**
 * Calculate which months a task spans across
 */
const getTaskMonthSpan = (startDate, endDate, dates, viewType) => {
  const spanColumns = [];
  const normalizedStartDate = normalizeDateString(startDate);
  const normalizedEndDate = normalizeDateString(endDate);

  dates.forEach((dateStr, colIndex) => {
    if (viewType === 'Quarter' || viewType === 'Year') {
      // Extract year and month from the column date string (YYYY-MM-DD format)
      const [colYear, colMonth] = dateStr.split('-').map(Number);
      
      // Create month boundary strings
      const monthStart = `${colYear}-${String(colMonth).padStart(2, '0')}-01`;
      const monthEnd = `${colYear}-${String(colMonth).padStart(2, '0')}-${new Date(colYear, colMonth, 0).getDate()}`;
      
      // String comparison for dates in YYYY-MM-DD format
      const overlaps = normalizedStartDate <= monthEnd && normalizedEndDate >= monthStart;
      
      if (overlaps) {
        spanColumns.push(colIndex);
      }
    }
  });
  
  return spanColumns;
};

/**
 * Check if two cards overlap based on their actual date ranges
 * @returns {boolean} - True if cards overlap
 */
const checkCardOverlap = (card1, card2) => {
  const getCardRange = (card) => {
    let earliestStart = card.tasks[0]?.startDate;
    let latestDue = card.tasks[0]?.dueDate;
    for (let i = 1; i < card.tasks.length; i++) {
      const taskStart = card.tasks[i].startDate;
      const taskDue = card.tasks[i].dueDate;
      if (taskStart && (!earliestStart || taskStart < earliestStart)) earliestStart = taskStart;
      if (taskDue && (!latestDue || taskDue > latestDue)) latestDue = taskDue;
    }
    const normalizedStart = normalizeDateString(earliestStart);
    const normalizedEnd = normalizeDateString(latestDue);
    return {
      start: parseDateLocal(normalizedStart),
      end: parseDateLocal(normalizedEnd)
    };
  };

  const range1 = getCardRange(card1);
  const range2 = getCardRange(card2);

  if (!range1.start || !range1.end || !range2.start || !range2.end) return false;

  // Overlap if date ranges intersect (inclusive)
  return range1.start <= range2.end && range2.start <= range1.end;
};

/**
 * Group tasks by their column spans and calculate positions for card view
 */
export const calculateCardPositions = (tasks, dates, viewType, getTaskDates) => {
  if (viewType !== 'Quarter' && viewType !== 'Year') {
    return {};
  }

  // Create individual task items with their span information
  const tasksWithCols = [];
  
  tasks.forEach(task => {
    if (!task.startDate || !task.dueDate) return;

    const { startDate, dueDate } = getTaskDates(task);
    const normalizedStartDate = normalizeDateString(startDate);
    const normalizedDueDate = normalizeDateString(dueDate);
    
    // Get all months the task spans (including those outside current view)
    const allSpanColumns = getTaskMonthSpan(normalizedStartDate, normalizedDueDate, dates, viewType);
    
    // Also check if task overlaps with current view even if it doesn't start/end in view
    const taskStartYM = normalizedStartDate.substring(0, 7); // YYYY-MM
    const taskEndYM = normalizedDueDate.substring(0, 7); // YYYY-MM
    const viewStartYM = dates[0].substring(0, 7);
    const viewEndYM = dates[dates.length - 1].substring(0, 7);
    
    // Calculate actual span width based on start and end months, not just visible columns
    const [startYear, startMonth] = normalizedStartDate.split('-').map(Number);
    const [endYear, endMonth] = normalizedDueDate.split('-').map(Number);
    const monthSpanCount = (endYear - startYear) * 12 + (endMonth - startMonth) + 1;
    const actualSpanWidth = Math.max(1, monthSpanCount);
    
    // Task overlaps with view if:
    // 1. It has columns in current view, OR
    // 2. It starts before view and ends after view start, OR
    // 3. It starts before view end and ends after view
    const overlapsView = allSpanColumns.length > 0 || 
                        (taskStartYM < viewStartYM && taskEndYM >= viewStartYM) ||
                        (taskStartYM <= viewEndYM && taskEndYM > viewEndYM);


    if (overlapsView) {
      // For tasks that span beyond view, adjust column indices
      let startCol, endCol;
      
      if (allSpanColumns.length > 0) {
        startCol = allSpanColumns[0];
        endCol = allSpanColumns[allSpanColumns.length - 1];
      } else {
        // Task spans entire view but doesn't start/end in visible months
        startCol = 0;
        endCol = dates.length - 1;
      }
      
      tasksWithCols.push({
        id: task.id,
        ticket: task.ticketNumber,
        task: { ...task, startDate: normalizedStartDate, dueDate: normalizedDueDate },
        startCol,
        endCol,
        spanWidth: actualSpanWidth,
        spanColumns: allSpanColumns.length > 0 ? allSpanColumns : [0, dates.length - 1],
        actualSpanWidth // Keep the actual span for grouping
      });
    }
  });

  // Sort by start column first, then by end column
  tasksWithCols.sort((a, b) => {
    if (a.startCol !== b.startCol) {
      return a.startCol - b.startCol;
    }
    return a.endCol - b.endCol;
  });

  // Group tasks by span type with proper month boundary validation
  const spanGroups = {};
  
  tasksWithCols.forEach(item => {
    const { startDate, dueDate } = item.task;
    const startMonth = startDate.substring(0, 7); // YYYY-MM
    const endMonth = dueDate.substring(0, 7); // YYYY-MM
    
    let spanKey;
    
    // Use actual span width for grouping logic, not visible span width
    if (item.actualSpanWidth <= 1) {
      // For single month cards, group by column and ensure same month
      spanKey = `single-${item.startCol}-${startMonth}`;
    } else {
      // For multi-month cards, group by exact span AND month boundaries
      spanKey = `multi-${item.startCol}-${item.endCol}-${startMonth}-${endMonth}`;
    }
    
    if (!spanGroups[spanKey]) {
      spanGroups[spanKey] = {
        tasks: [],
        startCol: item.startCol,
        endCol: item.endCol,
        spanWidth: item.spanWidth,
        spanColumns: item.spanColumns,
        startMonth,
        endMonth
      };
    }
    
    spanGroups[spanKey].tasks.push(item.task);
  });

  // Create cards and assign rows with proper overlap detection
  const allCards = [];
  const rowHeights = [];
  
  Object.values(spanGroups).forEach((group) => {
    // Create a single card for all tasks in the group
    if (group.tasks.length > 0) {
      // Calculate card height based on all tasks
      const baseHeight = 28;
      const itemHeight = 24;
      const cardHeight = baseHeight + (group.tasks.length * itemHeight);
      
      // Create card object for overlap checking and boundary calculation
      const card = {
        tasks: group.tasks,
        startCol: group.startCol,
        endCol: group.endCol,
        spanWidth: group.spanWidth,
        spanColumns: group.spanColumns
      };
      
      // Calculate boundary info for this card
      const boundaryInfo = getTaskBoundaryInfo(card, dates, viewType);
      
      // Find the lowest available row using actual position-based overlap detection
      let targetRow = 0;
      let rowFound = false;
      
      while (!rowFound) {
        let hasConflict = false;
        
        // Check against all existing cards in the same row
        for (const existingCard of allCards) {
          if (existingCard.rowIndex === targetRow) {
            const colOverlap = card.startCol <= existingCard.endCol && card.endCol >= existingCard.startCol;
            if (colOverlap || checkCardOverlap(card, existingCard)) {
              hasConflict = true;
              break;
            }
          }
        }
        
        if (!hasConflict) {
          rowFound = true;
        } else {
          targetRow++;
        }
      }
      
      // Update row height tracking
      if (!rowHeights[targetRow]) {
        rowHeights[targetRow] = 0;
      }
      rowHeights[targetRow] = Math.max(rowHeights[targetRow], cardHeight);
      
      allCards.push({
        id: `card-${group.startCol}-${group.endCol}`,
        tasks: group.tasks,
        totalTasks: group.tasks.length,
        startCol: group.startCol,
        endCol: group.endCol,
        spanWidth: group.spanWidth,
        spanColumns: group.spanColumns,
        colorIndex: parseInt(group.tasks[0].id) || 0,
        rowIndex: targetRow,
        cardHeight: cardHeight,
        boundaryInfo: boundaryInfo
      });
    }
  });
  
  // Calculate cumulative top positions based on actual row heights
  const rowTopPositions = [8];
  for (let i = 1; i < rowHeights.length; i++) {
    const previousRowTop = rowTopPositions[i - 1];
    const previousRowHeight = rowHeights[i - 1] || 60;
    rowTopPositions[i] = previousRowTop + previousRowHeight + 12;
  }
  
  // Update cards with calculated top positions
  allCards.forEach(card => {
    card.topPosition = rowTopPositions[card.rowIndex] || 8;
  });

  // Group cards by their starting columns for rendering
  const cardsByColumn = {};
  allCards.forEach(card => {
    if (!cardsByColumn[card.startCol]) {
      cardsByColumn[card.startCol] = [];
    }
    cardsByColumn[card.startCol].push(card);
  });

  return cardsByColumn;
};

/**
 * Calculate today's position within the Gantt view
 * @param {Array} dates - Array of date strings in the current view
 * @param {string} viewType - 'Day', 'Week', 'Quarter', or 'Year'
 * @returns {Object} - {position, isVisible} where position is CSS left value
 */
export const calculateTodayPosition = (dates, viewType) => {
  const today = new Date();
  const todayStr = formatDate(today);
  
  if (viewType === 'Day') {
    // Find exact date match
    const todayIndex = dates.findIndex(date => date === todayStr);
    if (todayIndex === -1) return { position: null, isVisible: false };
    
    // Day view: 500px min-width columns with 8px padding
    const columnWidth = 500;
    const columnPadding = 8;
    const position = `${todayIndex * columnWidth + columnWidth / 2 + columnPadding}px`;
    return { position, isVisible: true };
  }
  
  if (viewType === 'Week') {
    // Find which week contains today
    for (let i = 0; i < dates.length; i++) {
      const weekStartDate = parseDateLocal(dates[i]); // dates[i] is already Monday
      const weekEndDate = new Date(weekStartDate);
      weekEndDate.setDate(weekStartDate.getDate() + 6); // Sunday
      
      if (today >= weekStartDate && today <= weekEndDate) {
        // Week view: 500px min-width columns with 8px padding
        const columnWidth = 500;
        const columnPadding = 8;
        
        // Calculate position within the week (0 = Monday, 6 = Sunday)
        const dayOfWeek = today.getDay();
        const mondayBasedDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convert Sunday=0 to Sunday=6, Monday=1 to Monday=0
        
        // Position within the week column (each day is 1/7 of the column width)
        const dayWidth = (columnWidth - 2 * columnPadding) / 7;
        const dayPosition = mondayBasedDay * dayWidth + dayWidth / 2; // Center of the day
        
        const position = `${i * columnWidth + columnPadding + dayPosition}px`;
        return { position, isVisible: true };
      }
    }
    return { position: null, isVisible: false };
  }
  
  if (viewType === 'Quarter') {
    // Find which month contains today
    const todayMonth = today.getMonth() + 1; // 1-12
    const todayYear = today.getFullYear();
    
    for (let i = 0; i < dates.length; i++) {
      const [year, month] = dates[i].split('-').map(Number);
      
      if (year === todayYear && month === todayMonth) {
        // Quarter view: Use percentage-based positioning like other views
        const columnWidth = 100 / dates.length; // Each column is 33.33% for 3 months
        
        // Calculate position within the month
        const daysInMonth = new Date(todayYear, todayMonth, 0).getDate();
        const dayOfMonth = today.getDate();
        const monthProgress = (dayOfMonth - 1) / (daysInMonth - 1);
        
        // Use calc() to account for padding dynamically
        const position = `calc(${i * columnWidth}% + ${monthProgress * columnWidth}% + 12px)`;
        return { position, isVisible: true };
      }
    }
    return { position: null, isVisible: false };
  }
  
  if (viewType === 'Year') {
    // Find which month contains today
    const todayMonth = today.getMonth() + 1;
    const todayYear = today.getFullYear();
    
    for (let i = 0; i < dates.length; i++) {
      const [year, month] = dates[i].split('-').map(Number);
      if (year === todayYear && month === todayMonth) {
        // Year view: 180px min-width columns with 12px padding
        const columnWidth = 180;
        const columnPadding = 12;
        
        // Calculate position within the month
        const daysInMonth = new Date(todayYear, todayMonth, 0).getDate();
        const dayOfMonth = today.getDate();
        const monthProgress = (dayOfMonth - 1) / (daysInMonth - 1);
        
        const monthPosition = monthProgress * (columnWidth - 2 * columnPadding);
        const position = `${i * columnWidth + columnPadding + monthPosition}px`;
        return { position, isVisible: true };
      }
    }
    return { position: null, isVisible: false };
  }
  
  return { position: null, isVisible: false };
};

/**
 * Calculate date range for API calls based on target date and view type
 * @param {Date|string} targetDate - The target date to navigate to
 * @param {string} viewType - 'Day', 'Week', 'Quarter', or 'Year'
 * @returns {Object} - {fromDate, toDate} in YYYY-MM-DD format
 */
export const calculateDateRangeForView = (targetDate, viewType) => {
  const date = typeof targetDate === 'string' ? new Date(targetDate) : new Date(targetDate);
  const year = date.getFullYear();
  const month = date.getMonth();
  
  switch (viewType) {
    case 'Day':
      // For day view: target date ± 1 day (3 days total: prev, target, next)
      const dayStart = new Date(date);
      dayStart.setDate(date.getDate() - 1);
      const dayEnd = new Date(date);
      dayEnd.setDate(date.getDate() + 1);
      return { fromDate: formatDate(dayStart), toDate: formatDate(dayEnd) };
      
    case 'Week':
      // For week view: full month containing the target date with boundary weeks
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      
      // Find the first Monday of the month or the Monday before
      let weekStart = new Date(firstDay);
      const dayOfWeek = weekStart.getDay();
      const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      weekStart.setDate(firstDay.getDate() + daysToMonday);
      
      // Find the last Sunday that includes days from this month
      let weekEnd = new Date(lastDay);
      const lastDayOfWeek = weekEnd.getDay();
      const daysToSunday = lastDayOfWeek === 0 ? 0 : 7 - lastDayOfWeek;
      weekEnd.setDate(lastDay.getDate() + daysToSunday);
      
      return { fromDate: formatDate(weekStart), toDate: formatDate(weekEnd) };
      
    case 'Quarter':
      // For quarter view: full quarter containing the target date
      const quarterStartMonth = Math.floor(month / 3) * 3;
      const qFirstDay = new Date(year, quarterStartMonth, 1);
      const qLastDay = new Date(year, quarterStartMonth + 3, 0);
      return { fromDate: formatDate(qFirstDay), toDate: formatDate(qLastDay) };
      
    case 'Year':
      // For year view: full year
      return { fromDate: `${year}-01-01`, toDate: `${year}-12-31` };
      
    default:
      return { fromDate: null, toDate: null };
  }
};

/**
 * Returns the column range {startCol, endCol, isOutside, isBefore, isAfter}
 * for a task within the visible dates array.
 *
 * @param {Object} task - Task object
 * @param {Array} dates - Array of column date strings
 * @param {string} viewType - 'Day', 'Week', 'Quarter', 'Year'
 * @returns {Object|null}
 */
export const getTaskColumnRange = (task, dates, viewType) => {
  if (!task || !Array.isArray(dates) || dates.length === 0) return null;
  const s = task.startDate || task.start_date;
  const d = task.dueDate || task.due_date;
  if (!s && !d) return null;

  const start = parseDateLocal(s || d);
  const end = parseDateLocal(d || s);
  if (!start || !end) return null;

  const normalizedStart = start <= end ? start : end;
  const normalizedEnd = start <= end ? end : start;

  if (viewType === 'Week') {
    const viewStartDate = parseDateLocal(dates[0]);
    const lastDate = parseDateLocal(dates[dates.length - 1]);
    if (!viewStartDate || !lastDate) return null;

    const viewEndDate = new Date(lastDate);
    viewEndDate.setDate(lastDate.getDate() + 6);

    let startCol = -1;
    let endCol = -1;

    for (let i = 0; i < dates.length; i++) {
      const weekStart = parseDateLocal(dates[i]);
      if (!weekStart) continue;
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);

      if (normalizedStart <= weekEnd && normalizedEnd >= weekStart) {
        if (startCol === -1) startCol = i;
        endCol = i;
      }
    }

    if (startCol !== -1 && endCol !== -1) {
      return { startCol, endCol, isOutside: false };
    }

    if (normalizedEnd < viewStartDate) {
      return { startCol: 0, endCol: 0, isOutside: true, isBefore: true };
    }
    if (normalizedStart > viewEndDate) {
      return { startCol: dates.length - 1, endCol: dates.length - 1, isOutside: true, isAfter: true };
    }
    return null;
  } else if (viewType === 'Day') {
    const startStr = formatDate(normalizedStart);
    const endStr = formatDate(normalizedEnd);
    let startCol = dates.indexOf(startStr);
    let endCol = dates.indexOf(endStr);

    if (startCol === -1 && endCol === -1) {
      const firstDate = parseDateLocal(dates[0]);
      const lastDate = parseDateLocal(dates[dates.length - 1]);
      if (firstDate && normalizedEnd < firstDate) {
        return { startCol: 0, endCol: 0, isOutside: true, isBefore: true };
      }
      if (lastDate && normalizedStart > lastDate) {
        return { startCol: dates.length - 1, endCol: dates.length - 1, isOutside: true, isAfter: true };
      }
    }

    if (startCol === -1) startCol = 0;
    if (endCol === -1) endCol = dates.length - 1;
    return { startCol, endCol, isOutside: false };
  } else if (viewType === 'Quarter' || viewType === 'Year') {
    const startYear = normalizedStart.getFullYear();
    const startMonth = normalizedStart.getMonth();
    const endYear = normalizedEnd.getFullYear();
    const endMonth = normalizedEnd.getMonth();

    let startCol = -1;
    let endCol = -1;

    for (let i = 0; i < dates.length; i++) {
      const d = parseDateLocal(dates[i]);
      if (d) {
        const colDate = new Date(d.getFullYear(), d.getMonth(), 1);
        const taskStartMonth = new Date(startYear, startMonth, 1);
        const taskEndMonth = new Date(endYear, endMonth, 1);
        if (taskStartMonth <= colDate && taskEndMonth >= colDate) {
          if (startCol === -1) startCol = i;
          endCol = i;
        }
      }
    }

    if (startCol !== -1 && endCol !== -1) {
      return { startCol, endCol, isOutside: false };
    }
  }

  return null;
};

/**
 * Finds the target column index for tasks displayed on a page.
 * Used during pagination to retain horizontal timeline position or
 * automatically navigate to the date range containing the tickets.
 *
 * @param {Array} tasks - Array of tasks for the page
 * @param {Array} dates - Array of column dates (strings)
 * @param {string} viewType - 'Day', 'Week', 'Quarter', 'Year'
 * @returns {number} - Column index, or -1 if no dates found
 */
export const getTargetColumnForTasks = (tasks, dates, viewType) => {
  if (!Array.isArray(tasks) || tasks.length === 0 || !Array.isArray(dates) || dates.length === 0) {
    return -1;
  }

  const occupiedCols = new Set();

  const processTask = (task) => {
    const range = getTaskColumnRange(task, dates, viewType);
    if (range && !range.isOutside) {
      for (let c = range.startCol; c <= range.endCol; c++) {
        occupiedCols.add(c);
      }
    }
    if (task.subtasks?.length) {
      task.subtasks.forEach(processTask);
    }
  };

  tasks.forEach(processTask);

  if (occupiedCols.size === 0) {
    let hasBefore = false;
    let hasAfter = false;
    const checkOutside = (task) => {
      const range = getTaskColumnRange(task, dates, viewType);
      if (range?.isBefore) hasBefore = true;
      if (range?.isAfter) hasAfter = true;
      if (task.subtasks?.length) task.subtasks.forEach(checkOutside);
    };
    tasks.forEach(checkOutside);
    if (hasBefore) return 0;
    if (hasAfter) return dates.length - 1;
    return -1;
  }

  const sorted = Array.from(occupiedCols).sort((a, b) => a - b);
  return sorted[0];
};
